import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PhoneCall, MessageSquareText } from "lucide-react";
import { api, errorMessage } from "../lib/api";
import { dateTime, titleCase } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { StatusBadge } from "../components/StatusBadge";
import { SpecialDates, SuggestedCases } from "../components/AdviserExtras";
import { AdviserCard, useAdviserName, useMyAdviser } from "../components/AdviserCard";

interface Enquiry { id: string; type: string; topic: string; message: string; phone: string; preferred_time: string; status: string; created_at: string; notes: { by: string; note: string; at: string }[] }
const ADVISER_IMG = "https://images.pexels.com/photos/2702805/pexels-photo-2702805.jpeg?auto=compress&cs=tinysrgb&w=900";

export default function AdviserPage() {
  const qc = useQueryClient();
  const { data: opts } = useQuery<{ topics: string[]; slots: string[] }>({ queryKey: ["enquiry-options"], queryFn: () => api.get("/enquiries/options").then((r) => r.data) });
  const { data: history } = useQuery<Enquiry[]>({ queryKey: ["enquiries"], queryFn: () => api.get("/enquiries").then((r) => r.data) });
  const [mode, setMode] = useState<"callback" | "message">("callback");
  const [form, setForm] = useState({ topic: "", phone: "", preferred_time: "", message: "" });
  const [busy, setBusy] = useState(false);
  const { data: adviser } = useMyAdviser();
  const nm = useAdviserName();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", { ...form, type: mode, topic: form.topic || (opts?.topics[0] ?? "Wine recommendation") });
      const who = adviser && !/^the /i.test(adviser.name) ? adviser.first_name : "the Hourlier family";
      toast.success(mode === "callback" ? `Thank you — ${who} will call you.` : `Message sent — ${who} will reply personally.`);
      setForm({ topic: "", phone: "", preferred_time: "", message: "" });
      qc.invalidateQueries({ queryKey: ["enquiries"] });
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="adviser-page">
      <div className="mb-12"><SuggestedCases /></div>
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="eyebrow">Contact us</div>
          <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl" data-testid="adviser-heading">{adviser && !/^the /i.test(adviser.name) ? `${adviser.first_name} knows your cellar` : "A real person, who knows your cellar"}</h1>
          <p className="mt-4 text-sm leading-relaxed text-neutral-600">Ask {adviser && !/^the /i.test(adviser.name) ? adviser.first_name : "us"} to build a case for a dinner, find something for a birthday year, or simply talk wine. {adviser && !/^the /i.test(adviser.name) ? "They call" : "We call"} back within one working day.</p>
          <AdviserCard fallbackImg={ADVISER_IMG} />
        </div>
        <div className="lg:col-span-7">
          <div className="grid grid-cols-2 gap-px border border-border" data-testid="adviser-mode">
            {[["callback", "Request a call back", PhoneCall], ["message", "Send a message", MessageSquareText]].map(([v, label, Icon]: any) => (
              <button key={v} onClick={() => setMode(v)} className={`flex items-center justify-center gap-3 p-5 text-sm transition-colors duration-200 ${mode === v ? "bg-[var(--ink)] text-[var(--paper)]" : "hover:bg-[var(--parchment)]"}`} data-testid={`adviser-mode-${v}`}><Icon className="h-4 w-4" strokeWidth={1.25} />{label}</button>
            ))}
          </div>
          <form onSubmit={submit} className="mt-8 space-y-6" data-testid="adviser-form">
            <div>
              <div className="eyebrow">What's it about?</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {(opts?.topics || []).map((t) => <button type="button" key={t} onClick={() => setForm({ ...form, topic: t })} className={`border px-3 py-2 text-sm transition-colors duration-200 ${(form.topic || opts?.topics[0]) === t ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" : "border-border hover:border-[var(--ink)]"}`} data-testid={`adviser-topic-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{t}</button>)}
              </div>
            </div>
            {mode === "callback" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div><div className="eyebrow">Phone number</div><Input type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="07…" className="mt-2 rounded-none bg-transparent" data-testid="adviser-phone-input" /></div>
                <div><div className="eyebrow">Best time</div>
                  <select value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className="mt-2 w-full border border-border bg-transparent px-3 py-2 text-sm" data-testid="adviser-time-select">
                    <option value="">Any time</option>{(opts?.slots || []).map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            )}
            <div>
              <div className="eyebrow">{mode === "callback" ? "Anything we should know first? (optional)" : "Your message"}</div>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} required={mode === "message"} placeholder={mode === "callback" ? "e.g. Dinner for eight on Saturday, mostly red, budget around £150" : "Tell us what you're after…"} className="mt-2 w-full border border-border bg-transparent p-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--wine)]" data-testid="adviser-message-input" />
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-none py-6 text-base hover-lift" data-testid="adviser-submit-button">{busy ? "Sending…" : mode === "callback" ? `Ask ${nm.named ? nm.first : "us"} to call me` : `Send to ${nm.named ? nm.first : "the family"}`}</Button>
          </form>
        </div>
      </div>

      {history && history.length > 0 && (
        <section className="mt-16" data-testid="adviser-history">
          <h2 className="font-serif text-2xl sm:text-3xl">Your conversations</h2>
          <div className="mt-6 border-t border-border">
            {history.map((e) => (
              <div key={e.id} className="grid gap-3 border-b border-border py-4 text-sm sm:grid-cols-[140px_1fr_auto]" data-testid={`enquiry-row-${e.id}`}>
                <span className="text-neutral-500">{dateTime(e.created_at)}</span>
                <div><span className="font-medium">{e.type === "callback" ? "Call back" : "Message"} · {e.topic}</span>{e.type === "callback" && <span className="text-neutral-500"> · {e.phone}{e.preferred_time ? ` · ${e.preferred_time}` : ""}</span>}{e.message && <p className="mt-1 text-neutral-600">{e.message}</p>}
                  {e.notes.map((n, i) => <p key={`${n.at}-${n.by}-${i}`} className="mt-2 border-l-2 border-[var(--wine)] pl-3 font-serif italic text-neutral-700">“{n.note}” <span className="not-italic text-xs text-neutral-500" data-testid="adviser-note-signature">— {(n as any).by_name || nm.who}, {dateTime(n.at)}</span></p>)}
                </div>
                <StatusBadge status={e.status} testId={`enquiry-status-${e.id}`} />
              </div>
            ))}
          </div>
        </section>
      )}
      <p className="mt-10 hidden">{titleCase("")}</p>
      <SpecialDates />
    </div>
  );
}
